import type { User } from "@/api/hooks/useMe";
import { usePushNotifications } from "@/api/hooks/usePushNotification";
import type { Session, Unit } from "@/api/raptor";
import { useApi, useAuth, useGetOne } from "@mb-pro-ui/utils";
import HailIcon from "@mui/icons-material/Hail";
import LogoutIcon from "@mui/icons-material/Logout";
import ReloadIcon from "@mui/icons-material/RestartAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  SwipeableDrawer,
  Switch,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "wouter";
import { useAudio } from "../contexts/AudioContexts";
import { useLocale } from "../locales/LocaleProvider";

const fields = { session: ["id", "start-ts", "unit"] } as const;
const include = { unit: {} } as const;

interface SessionWithUnit extends Omit<Session, "unit"> {
  unit: Unit;
}

const ShiftInfo = ({ me }: { me: User | undefined }) => {
  const { enabled, enablePush, disablePush, checkToken } =
    usePushNotifications();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const [_, navigate] = useLocation();
  const { locale, locales, setLocale } = useLocale();
  const api = useApi();
  const auth = useAuth();
  const { shutdownAudio } = useAudio();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<number | null>(null);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const { data, isLoading } = useGetOne<SessionWithUnit>(
    "raptor/sessions",
    "current",
    {
      enabled: drawerOpen,
      fields,
      include,
    },
  );

  const { mutate: stopSession } = useMutation({
    mutationFn: () => api(`/actions/raptor/stop-session`, { method: "POST" }),
    onSuccess: () => {
      queryClient.clear();
      handleDrawerToggle(false);
      shutdownAudio();
      navigate("/start-session", { replace: true });
    },
    onError: (err) => console.error("Hiba a műszak befejezésénél:", err),
  });

  const handleDrawerToggle = (isOpen: boolean) => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (isOpen && themeColorMeta) {
      setDrawerOpen(true);
      themeColorMeta.setAttribute("content", "#7e7e7e");
    } else {
      setDrawerOpen(false);
      themeColorMeta?.setAttribute("content", "#fcfcfc");
    }
  };

  const handleLogout = (stopShift = false) => {
    const performLogout = () => {
      auth.signout.mutate(undefined, {
        onSuccess: () => {
          queryClient.clear();
          shutdownAudio();
          navigate("/login", { replace: true });
        },
      });
    };

    if (stopShift && data) {
      stopSession();
    }
    performLogout();
  };

  const formatElapsedTime = (millis: number) => {
    const secondsTotal = Math.floor(millis / 1000);
    const hours = Math.floor((secondsTotal % (3600 * 24)) / 3600);
    const minutes = Math.floor((secondsTotal % 3600) / 60);
    const seconds = secondsTotal % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const handlePushToggle = async (checked: boolean) => {
    setSwitchLoading(true);
    try {
      if (checked) {
        await enablePush();
      } else {
        await disablePush();
      }
      await checkToken();
    } catch (error) {
      console.error("Push notification toggle failed:", error);
    } finally {
      setSwitchLoading(false);
    }
  };

  const handleLanguageSelect = (lang: string) => {
    setLocale(lang);
    localStorage.setItem("lang", lang);
  };

  const startTs = data?.["start-ts"];
  useEffect(() => {
    const interval = setInterval(() => {
      if (startTs) {
        const now = new Date();
        setElapsedTime(now.getTime() - new Date(startTs).getTime());
      }
    }, 1000);
    return () => {
      clearInterval(interval);
      setElapsedTime(null);
    };
  }, [startTs]);

  return (
    <>
      <IconButton
        sx={{ width: 30, height: 30, cursor: "pointer" }}
        onClick={() => handleDrawerToggle(true)}
      >
        <SettingsIcon sx={{ fontSize: 26 }} />
      </IconButton>

      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => handleDrawerToggle(false)}
        onOpen={() => {}}
        disableSwipeToOpen
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "65vh",
              background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
              px: 3,
              pt: 2,
              pb: 3,
            },
          },
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                width: 48,
                height: 4,
                bgcolor: "grey.300",
                borderRadius: 2,
                mx: "auto",
                mb: 3,
              }}
            />

            <Typography variant="raptor-main-h" align="center" sx={{ mb: 3 }}>
              {formatMessage({ defaultMessage: "Shift" })}
            </Typography>

            <Stack spacing={1}>
              <Box>
                <Typography
                  variant="raptor-sub-h"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {formatMessage({ defaultMessage: "User" })}
                </Typography>
                <Typography variant="raptor-main-h" fontWeight={500}>
                  {me?.name}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="raptor-sub-h"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {formatMessage({ defaultMessage: "Unit" })}
                </Typography>
                <Typography variant="raptor-main-h" fontWeight={500}>
                  {data?.unit.name}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="raptor-sub-h"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {formatMessage({ defaultMessage: "Shift start" })}
                </Typography>
                <Typography variant="raptor-main-h" sx={{ fontWeight: 500 }}>
                  {startTs ? new Date(startTs).toLocaleString("hu-HU") : "—"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="raptor-sub-h"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {formatMessage({ defaultMessage: "Elapsed time" })}
                </Typography>
                <Typography variant="raptor-main-h" sx={{ fontWeight: 500 }}>
                  {elapsedTime ? formatElapsedTime(elapsedTime) : "—"}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    disabled={switchLoading}
                    onChange={(e) => handlePushToggle(e.target.checked)}
                  />
                }
                label={formatMessage({ defaultMessage: "Notifications" })}
              />

              <Box
                sx={(theme) => ({
                  alignSelf: "flex-end",
                  color: theme.palette.text.secondary,
                  position: "absolute",
                  top: "30px",
                  right: "30px",
                  [theme.breakpoints.down("sm")]: {
                    top: "10px",
                    right: "10px",
                  },
                })}
              >
                {locales.map((lang) => (
                  <ButtonBase
                    sx={{
                      "&:not(:first-of-type):before": {
                        content: '"/"',
                        paddingLeft: "0.2rem",
                        paddingRight: "0.2rem",
                        textDecoration: "initial",
                      },
                    }}
                    key={lang}
                    onClick={() => handleLanguageSelect(lang)}
                    disabled={locale === lang}
                  >
                    <Typography
                      sx={[locale === lang && { fontWeight: "bold" }]}
                    >
                      {lang}
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
              <Box
                sx={(theme) => ({
                  alignSelf: "flex-start",
                  position: "absolute",
                  top: "30px",
                  left: "30px",
                  [theme.breakpoints.down("sm")]: {
                    top: "10px",
                    left: "10px",
                  },
                })}
              >
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  startIcon={<ReloadIcon />}
                  onClick={() => {
                    window.location.reload();
                  }}
                  sx={{ minHeight: "unset" }}
                >
                  v{APP_VERSION}
                </Button>
              </Box>

              <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={<HailIcon />}
                onClick={() => stopSession()}
                disabled={isLoading}
              >
                <Typography variant="raptor-button-text">
                  {formatMessage({ defaultMessage: "Shift end" })}
                </Typography>
              </Button>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <Divider sx={{ width: "80px" }} />
                <Typography textAlign="center">
                  {formatMessage({ defaultMessage: "or" })}
                </Typography>
                <Divider sx={{ width: "80px" }} />
              </Box>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<LogoutIcon />}
                onClick={() => setLogoutModalOpen(true)}
              >
                <Typography variant="raptor-button-text" color="primary">
                  {formatMessage({ defaultMessage: "Logout" })}
                </Typography>
              </Button>
            </Stack>
          </>
        )}
        <Dialog
          open={logoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
        >
          <DialogTitle>
            {formatMessage({ defaultMessage: "Logout" })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {formatMessage({
                defaultMessage: "Do you want to end your shift as well?",
              })}
            </DialogContentText>
          </DialogContent>
          <Stack sx={{ pl: 3, pr: 3, pb: 1 }} spacing={1}>
            <Button
              onClick={() => {
                handleLogout(false);
                setLogoutModalOpen(false);
              }}
              variant="contained"
            >
              {formatMessage({ defaultMessage: "Logout" })}
            </Button>
            <Button
              color="error"
              onClick={() => {
                handleLogout(true);
                setLogoutModalOpen(false);
              }}
            >
              {formatMessage({ defaultMessage: "End Shift & Logout" })}
            </Button>
          </Stack>
        </Dialog>
      </SwipeableDrawer>
    </>
  );
};

export default ShiftInfo;
